import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initAuth = async () => {
      try {
        const csrfToken = getCsrfToken();
        const response = await axios.post(`${API}/auth/refresh`, null, {
          headers: csrfToken ? { 'X-CSRF-Token': csrfToken } : {},
          withCredentials: true
        });
        const { access_token, user: userData } = response.data;
        setToken(access_token);
        setUser(userData);
      } catch (error) {
        setToken(null);
        setUser(null);
      }
      setLoading(false);
    };
    initAuth();
  }, []);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
    const { access_token, user: userData } = response.data;
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const logout = () => {
    const csrfToken = getCsrfToken();
    axios.post(`${API}/auth/logout`, null, {
      headers: csrfToken ? { 'X-CSRF-Token': csrfToken } : {},
      withCredentials: true
    }).catch(() => {});
    setToken(null);
    setUser(null);
  };

  const getAuthHeaders = () => ({
    Authorization: token ? `Bearer ${token}` : undefined
  });

  return (
    <AuthContext.Provider value={{ user, token, loading, login, logout, getAuthHeaders, isAdmin: user?.role === 'admin' }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

const getCsrfToken = () => {
  const match = document.cookie.split(';').map(c => c.trim()).find(c => c.startsWith('abkasan_csrf='));
  if (!match) return null;
  return decodeURIComponent(match.split('=')[1] || '');
};
